import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useProgramContext } from '../../../layouts/ProgramLayout';
import { programRef } from '../../../lib/access/types';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { formatMoney } from '../_shared/lib/recordLabels';
import { growthHooks } from './hooks';
const statusOptions = [
    {
        value: 'Identified',
        label: 'Identified'
    },
    {
        value: 'Pursuing',
        label: 'Pursuing'
    },
    {
        value: 'Won',
        label: 'Won'
    },
    {
        value: 'Lost',
        label: 'Lost'
    }
];
const columns = [
    {
        key: 'description',
        header: 'Description'
    },
    {
        key: 'status',
        header: 'Status'
    },
    {
        key: 'estimatedValue',
        header: 'Estimated value',
        render: (row)=>formatMoney(row.estimatedValue),
        maskAs: 'estimated_value'
    }
];
const fields = [
    {
        kind: 'textarea',
        name: 'description',
        label: 'Description',
        required: true,
        rows: 3
    },
    {
        kind: 'select',
        name: 'status',
        label: 'Status',
        required: true,
        options: statusOptions
    },
    {
        kind: 'number',
        name: 'estimatedValue',
        label: 'Estimated value ($)',
        min: 0
    },
    {
        kind: 'textarea',
        name: 'comment',
        label: 'Comment',
        rows: 2
    }
];
export function GrowthOpportunitiesTab({ programId }) {
    const { canEdit } = useProgramContext();
    const { data, isLoading } = growthHooks.useList({
        programId
    });
    const upsert = growthHooks.useUpsert();
    const remove = growthHooks.useDelete();
    const items = data?.items ?? [];
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [detailId, setDetailId] = useState(null);
    // Derive the viewed record from `items` (store only the id) so an open
    // drawer always reflects the latest list without a re-sync effect that
    // infinite-loops when the list returns fresh-but-equal refs each render.
    const detail = detailId ? items.find((r)=>r.id === detailId) ?? null : null;
    // Whether the detail drawer opens in read or straight into edit (row Edit).
    const [detailMode, setDetailMode] = useState('read');
    // Shared by the create modal and the drawer edit-in-place; the target id is
    // passed in (empty = create) since the two surfaces track different records
    // (modal → `editing`, drawer → `detail`).
    const submitGrowth = async (values, id)=>{
        await upsert.mutateAsync({
            id,
            programId,
            description: values.description,
            status: values.status,
            estimatedValue: Number(values.estimatedValue) || 0,
            comment: values.comment ?? ''
        });
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    // The drawer's inline-edit config, bound to the record being viewed. The
    // editable controls live on the rows (below); this carries only the form-level
    // concerns.
    const detailEdit = detail ? {
        defaultValues: {
            id: detail.id,
            description: detail.description ?? '',
            status: detail.status ?? 'Identified',
            estimatedValue: detail.estimatedValue ?? 0,
            comment: detail.comment ?? ''
        },
        onSubmit: (v)=>submitGrowth(v, detail.id),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save changes'
    } : undefined;
    // Description/Status/Estimated value are triage columns; Comment is the
    // overflow field — all four carry an inline `editField` so the drawer edits
    // in place. Estimated value stays masked in read (the drawer honors the same
    // field masking the table applies).
    const detailSections = detail ? [
        {
            heading: 'Opportunity',
            rows: [
                {
                    label: 'Description',
                    value: detail.description || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'description',
                        label: 'Description',
                        required: true,
                        rows: 3
                    }
                },
                {
                    label: 'Status',
                    value: detail.status || '—',
                    editField: {
                        kind: 'select',
                        name: 'status',
                        label: 'Status',
                        required: true,
                        options: statusOptions
                    }
                },
                {
                    label: 'Estimated value',
                    value: formatMoney(detail.estimatedValue),
                    masked: true,
                    editField: {
                        kind: 'number',
                        name: 'estimatedValue',
                        label: 'Estimated value ($)',
                        min: 0
                    }
                },
                {
                    label: 'Comment',
                    value: detail.comment || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'comment',
                        label: 'Comment',
                        rows: 2
                    }
                }
            ]
        }
    ] : [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: ()=>{
                        setEditing(null);
                        setFormOpen(true);
                    },
                    disabled: !canEdit,
                    "data-testid": Testids.deliveryRisk.growthCreate,
                    children: "New Opportunity"
                })
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: items,
                columns: columns,
                testid: Testids.deliveryRisk.growthTable,
                rowTestid: Testids.deliveryRisk.growthRow,
                rowKey: (g)=>g.id,
                isLoading: isLoading,
                resource: programRef(programId),
                onRowClick: (row)=>{
                    setDetailMode('read');
                    setDetailId(row.id);
                },
                rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>{
                                    setDetailMode('read');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryRisk.growthDetails,
                                children: "Details"
                            }),
                            canEdit && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        // Edit-in-place: open the drawer straight into edit mode.
                                        onClick: ()=>{
                                            setDetailMode('edit');
                                            setDetailId(row.id);
                                        },
                                        "data-testid": Testids.deliveryRisk.growthEdit,
                                        children: "Edit"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        className: "text-danger hover:bg-danger-muted hover:text-danger",
                                        onClick: ()=>setDeleting(row),
                                        "data-testid": Testids.deliveryRisk.growthDelete,
                                        children: "Delete"
                                    })
                                ]
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: editing ? 'Edit Opportunity' : 'New Opportunity',
                fields: fields,
                defaultValues: {
                    id: editing?.id ?? '',
                    description: editing?.description ?? '',
                    status: editing?.status ?? 'Identified',
                    estimatedValue: editing?.estimatedValue ?? 0,
                    comment: editing?.comment ?? ''
                },
                onSubmit: async (v)=>{
                    await submitGrowth(v, editing?.id ?? '');
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Opportunity",
                description: deleting ? 'Delete this growth opportunity? This cannot be undone.' : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            }),
            /*#__PURE__*/ _jsx(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: "Growth Opportunity",
                sections: detailSections,
                testid: Testids.deliveryRisk.growthDetailDrawer,
                resource: programRef(programId),
                maskAs: "estimated_value",
                edit: detailEdit,
                canEdit: canEdit,
                initialMode: detailMode
            })
        ]
    });
}
